import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import BookingWizard from "@/components/BookingWizard";
import { SITE, WHATSAPP_LINK } from "@/config/site";

const WhatsAppGlyph = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
);

const Contatti = () => (
    <div data-testid="contatti-page">
        <SEO
            title="Contatti e Prenotazioni | Studio Olistico Bologna"
            description="Contatta lo studio olistico a Bologna: telefono, WhatsApp, email e orari. Prenota il tuo massaggio olistico su appuntamento."
        />

        <header className="mx-auto max-w-6xl px-5 pb-16 pt-36 md:px-8 md:pt-44">
            <Reveal>
                <Overline>Contatti</Overline>
                <h1 className="mt-5 max-w-2xl font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="contatti-title">
                    Il primo passo è <span className="italic text-sage">una conversazione</span>
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-clay">
                    Scrivimi per qualsiasi domanda, oppure prenota direttamente online: la conferma arriva
                    subito nella tua email.
                </p>
            </Reveal>
        </header>

        <section className="mx-auto max-w-6xl px-5 pb-24 md:px-8">
            <div className="grid gap-6 sm:grid-cols-2" data-testid="contatti-cards">
                {[
                    {
                        icon: Phone,
                        title: "Telefono",
                        value: SITE.phoneDisplay,
                        href: SITE.phoneHref,
                        testid: "contact-phone",
                        note: "Chiamami per qualsiasi domanda",
                    },
                    {
                        icon: WhatsAppGlyph,
                        title: "WhatsApp",
                        value: "Scrivimi direttamente",
                        href: WHATSAPP_LINK,
                        testid: "contact-whatsapp",
                        external: true,
                        note: "Risposta appena possibile",
                    },
                ].map((c, i) => (
                    <Reveal key={c.title} delay={i * 0.08}>
                        <a
                            href={c.href}
                            {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                            data-testid={c.testid}
                            className="group block h-full rounded-2xl border border-border bg-card p-7 shadow-[0_4px_20px_rgba(67,46,30,0.05)] transition-shadow duration-500 hover:shadow-[0_12px_40px_rgba(82,117,87,0.14)]"
                        >
                            <c.icon className="h-6 w-6 text-sage" strokeWidth={1.5} />
                            <h2 className="mt-4 font-serif text-xl text-ink">{c.title}</h2>
                            <p className="mt-2 break-words text-sm font-medium text-clay transition-colors group-hover:text-sage">
                                {c.value}
                            </p>
                            <p className="mt-1 text-xs italic text-fog">{c.note}</p>
                        </a>
                    </Reveal>
                ))}
            </div>

            <div className="mt-16 grid gap-10 lg:grid-cols-2 lg:gap-16">
                <Reveal>
                    <div className="h-full rounded-2xl border border-border bg-card p-8 shadow-[0_4px_20px_rgba(67,46,30,0.05)]" data-testid="contact-info-panel">
                        <div className="flex items-start gap-4">
                            <MapPin className="mt-1 h-5 w-5 shrink-0 text-sage" strokeWidth={1.5} />
                            <div>
                                <h2 className="font-serif text-2xl text-ink">Dove</h2>
                                <p className="mt-2 text-sm leading-relaxed text-clay" data-testid="contact-address">
                                    {SITE.address}
                                </p>
                            </div>
                        </div>
                        <div className="mt-8 flex items-start gap-4">
                            <Clock className="mt-1 h-5 w-5 shrink-0 text-sage" strokeWidth={1.5} />
                            <div className="w-full">
                                <h2 className="font-serif text-2xl text-ink">Orari</h2>
                                <ul className="mt-3 space-y-2 text-sm text-clay" data-testid="contact-hours">
                                    {SITE.hours.map((h) => (
                                        <li key={h.days} className="flex justify-between gap-4 border-b border-border/60 pb-2">
                                            <span>{h.days}</span>
                                            <span className="text-fog">{h.time}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <Link
                            to="/prenota"
                            data-testid="contact-cta-prenota"
                            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark"
                        >
                            Prenota il tuo trattamento
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                        </Link>
                    </div>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="h-full overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(67,46,30,0.08)]">
                        <iframe
                            title="Mappa dello studio a Bologna"
                            src={SITE.mapsEmbed}
                            className="h-full min-h-[380px] w-full border-0"
                            loading="lazy"
                            data-testid="contact-map"
                        />
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="bg-sand py-20 md:py-24" data-testid="contatti-booking-section">
            <div className="mx-auto max-w-4xl px-5 md:px-8">
                <Reveal>
                    <Overline>Prenota online</Overline>
                    <h2 className="mt-4 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
                        Il modulo di prenotazione
                    </h2>
                    <p className="mt-4 max-w-xl text-sm leading-relaxed text-clay">
                        Tutti i campi sono obbligatori e la prenotazione richiede un preavviso di almeno 1
                        giorno. Riceverai subito una email di conferma automatica e la tua richiesta
                        arriva direttamente allo studio.
                    </p>
                </Reveal>
                <BookingWizard />
            </div>
        </section>
    </div>
);

export default Contatti;
